import type { ReactNode } from "react";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { Icon } from "../Icons/Icon";
import { Tooltip } from "../Feedback/Tooltip";
import { CopyableValue } from "../Display/CopyableValue";
import { CopyableProvider } from "../Display/Internal/CopyableContext";
import { BadgeSizeContext } from "../Display/BadgeSizeContext";
import { formatStatValue, isEmptyStatValue } from "./Helpers/formatStatValue";
import type { StatEntry } from "./Types/StatItemType";
import { isStatDivider } from "./Types/StatItemType";
import { StatValueButton_ } from "./Internal/StatValueButton";
import { PanelCornerNumber_ } from "./Internal/PanelCornerNumber";
import { PanelCornerNumberSpace_ } from "./Internal/PanelCornerNumberSpace";
import { visibleCornerNumber } from "./Internal/visibleCornerNumber";
import { PanelContext } from "./PanelContext";
import { PageEdgeContext } from "./Internal/PageEdgeContext";
import { ControlBar } from "./ControlBar";
import { headingFontStyle } from "../Theme/headingFontStyle";
import { layerClass } from "../Theme/layers";

type AccentColor = "red" | "gold" | "blue" | "green" | "purple";
type PanelAccent = AccentColor | "none";

const accentBorder: Record<AccentColor, string> = {
  red: "border-l-red",
  gold: "border-l-orange",
  blue: "border-l-blue",
  green: "border-l-green",
  purple: "border-l-purple",
};

const accentBadge: Record<AccentColor, string> = {
  red: "bg-very-light-red text-danger-strong",
  gold: "bg-very-light-orange text-warning-strong",
  blue: "bg-blue/10 text-info-strong",
  green: "bg-green/10 text-success-strong",
  purple: "bg-purple/10 text-purple",
};

function PanelStats_({ stats }: { stats: StatEntry[] }) {
  // Comfortable density: align the stats' left edge with the title
  // (px-6, matching the header) and drop the divider rule beneath them.
  // Top-align the cells (items-start) so every stat's label sits on the same
  // line even when one value wraps to multiple lines (e.g. a 3-line address).
  return (
    <CopyableProvider value="reveal">
      {/* shrinks any Badge used as a stat value so badge-valued stats
          don't tower over the numeric ones */}
      <BadgeSizeContext.Provider value="small">
        <div className="flex flex-wrap items-start gap-x-6 gap-y-2 px-6 pb-4">
          {stats.map((entry) => {
            if (isStatDivider(entry)) {
              return (
                <div
                  key={entry.dividerId}
                  className="h-7 w-px shrink-0 bg-foreground/15"
                />
              );
            }
            const item = entry;
            return (
              <div key={item.statKey ?? String(item.label)}>
                <div className="flex items-center gap-1 text-[10px] font-semibold tracking-wide text-content-muted uppercase">
                  {item.label}
                  {item.tooltip && (
                    <Tooltip variant="info" size="sm" content={item.tooltip} />
                  )}
                </div>
                <div className="mt-0.5 text-sm font-bold text-foreground">
                  {item.onClick ? (
                    <StatValueButton_ item={item} />
                  ) : (
                    <CopyableValue
                      value={formatStatValue(item)}
                      copyValue={item.copyValue}
                      copyOptions={item.copyOptions}
                      label={item.label}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </BadgeSizeContext.Provider>
    </CopyableProvider>
  );
}

interface PanelProps {
  /** Panel heading. Omit for a plain bordered card with no header band. */
  title?: ReactNode | undefined;
  description?: ReactNode;
  count?: number | undefined;
  /** Compact stats shown inline in the header row, between title and controls. */
  headerStats?: StatEntry[] | undefined;
  /** Right-aligned labeled filter controls */
  headerFilters?: ReactNode;
  /** Right-aligned action buttons */
  headerActions?: ReactNode;
  /** Menu items — auto-wrapped in a Menu kebab. Pass Button children directly. */
  menu?: ReactNode;
  /** Left-aligned action buttons in the header controls row. */
  leftActions?: ReactNode;
  /** Full-width filter/control bar below the header, separated by a border. */
  filterBar?: ReactNode;
  statsBar?: StatEntry[] | undefined;
  children?: ReactNode;
  /** When true, the panel content is collapsible */
  collapsible?: boolean | undefined;
  /** Colored left border accent (only shown when collapsible). Pass `"none"` to drop the colored accent — the panel keeps a regular border and the count badge falls back to neutral grey. */
  accent?: PanelAccent | undefined;
  /** Whether the collapsible panel starts open (default: true) */
  defaultOpen?: boolean | undefined;
  /** When true, children render without padding and tables drop their border */
  fullBleed?: boolean | undefined;
  /**
   * With `fullBleed`, drop the body's horizontal gutter and push it into the
   * table cells instead, so table rows (hover, highlight, header rule) span the
   * full card edge-to-edge while cell content stays aligned. Opt-in: existing
   * panels keep the inset rows.
   */
  edgeToEdge?: boolean | undefined;
  /** Dynamic CSS color for the left border accent. Overrides `accent` when provided. Only applies when `collapsible` is true. */
  accentColor?: string | undefined;
  /** When true, the collapsible header becomes sticky (position: sticky; top: 0). Use inside scrollable containers such as dialogs. */
  stickyHeader?: boolean | undefined;
  /**
   * On a narrow container, shed the frame — border, shadow, and body padding —
   * so the content gets the full width instead of a box squeezing it. Keyed on
   * a `.narrow-cards` ancestor class, which the host toggles from a width
   * measurement (the booking flow flips it at the same 640px its outer card
   * goes flush). Off by default; only honored for non-collapsible panels — a
   * collapsible panel's header chrome has nothing sensible to collapse into.
   */
  collapseOnNarrow?: boolean | undefined;
  /**
   * A number pinned in a triangle on the top-left corner, for telling apart a
   * run of identical panels such as a shipment's line items. The triangle fits
   * two digits. The body moves down to clear it. Ignored on a collapsible
   * panel or one with a header, filter bar or stats bar, since those already
   * own that corner.
   */
  cornerNumber?: number | undefined;
}

// Kept literal (not interpolated) so Tailwind's JIT can see each class. Keyed
// on a `.narrow-cards` ancestor class rather than a container query: a
// `container-type` ancestor would apply layout containment, which re-bases
// fixed-position popovers inside the panel to the container's corner.
const collapseFrameClasses =
  " in-[.narrow-cards]:border-transparent in-[.narrow-cards]:shadow-none";
const collapseBodyClasses = " in-[.narrow-cards]:p-0";

function PanelHeaderStats_({ items }: { items: StatEntry[] }) {
  return (
    <BadgeSizeContext.Provider value="small">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        {items.map((entry) => {
          if (isStatDivider(entry)) {
            return (
              <div
                key={entry.dividerId}
                className="h-6 w-px shrink-0 bg-border"
              />
            );
          }
          const item = entry;
          return (
            <div
              key={item.statKey ?? String(item.label)}
              className="flex items-baseline gap-1.5"
            >
              <span className="text-[11px] font-semibold tracking-wide whitespace-nowrap text-content-muted uppercase">
                {item.label}
              </span>
              <span className="text-sm font-bold whitespace-nowrap text-foreground">
                {item.percentage ? (
                  `${item.percentage.value.toFixed(1)}%`
                ) : isEmptyStatValue(item.value) ? (
                  <span className="text-content-faint">{"—"}</span>
                ) : (
                  item.value
                )}
              </span>
            </div>
          );
        })}
      </div>
    </BadgeSizeContext.Provider>
  );
}

function PanelHeader_({
  title,
  description,
  count,
  headerStats,
  headerFilters,
  headerActions,
  menu,
  leftActions,
}: {
  title?: ReactNode | undefined;
  description?: ReactNode;
  count?: number | undefined;
  headerStats?: StatEntry[] | undefined;
  headerFilters?: ReactNode;
  headerActions?: ReactNode;
  menu?: ReactNode;
  leftActions?: ReactNode;
}) {
  // Something on the right means the controls cluster below is worth space; with
  // nothing there it must not exist, or its reserved basis would wrap titles
  // early for no reason. This tests props, not rendered output — a Panel can't
  // measure its children here, which is why ActionBar does the measuring. So
  // `headerActions={<>{cond && <Button/>}</>}` counts as controls even when the
  // condition is false and it renders nothing. To opt a panel out, pass
  // undefined instead of an empty fragment.
  const hasControls = !!headerFilters || !!headerActions || !!menu;
  const hasStats = (headerStats?.length ?? 0) > 0;
  // With stats in the row the title block takes a zero basis and grows into the
  // slack instead of claiming its max-content. That keeps free space positive at
  // normal widths, so nothing shrinks and the stats sit at their natural width;
  // once the row really is too small the stats become the only thing that can
  // yield, and they wrap rather than escape. Its floor is min-content (no
  // min-w-0), so the title never spills its own box either. Without stats it
  // keeps exactly what ENG-6540 left it.
  const titleBlockClass = hasStats
    ? "flex flex-1 items-center gap-5"
    : "flex min-w-0 items-center gap-5";
  // flex-wrap is the last resort under the stats, and only reachable with them.
  // A line is packed by each item's hypothetical size — the basis clamped by
  // its minimum — so the title contributes its min-content, i.e. its longest
  // word, NOT its zero basis. Measured, the break sits at that longest word
  // plus the stats' natural width plus the gap and the card's padding, moving
  // one-for-one with the title: 68px longest word breaks at 529px, 150px at
  // 612px, 386px at 847px. The cluster isn't a term — it shrinks, so it takes
  // its own line rather than holding one open. Above the break the row is
  // identical to the nowrap one; below it the stats drop to their own line
  // instead of pushing themselves and the kebab outside the card, and the
  // title gets its width back.
  //
  // No divider under the header, so it carries the separation as whitespace —
  // hence the roomier bottom pad. Comfortable density (Cargo card): a larger
  // title and generous padding.
  const headerRowClass = hasStats
    ? "flex flex-wrap items-center justify-between gap-3 px-6 pt-5 pb-5"
    : "flex items-center justify-between gap-3 px-6 pt-5 pb-5";
  return (
    <div className={headerRowClass}>
      <div className={titleBlockClass}>
        <div className="min-w-0">
          {/* gap-2 (not the text's own space): a flex item's trailing
              whitespace is trimmed, so a title like `Notes for <Carrier/>` would
              butt straight against the logo. An explicit gap restores a uniform
              space between every title part (text, inline logo, count badge). */}
          <h3
            className={`flex flex-wrap items-center gap-2 text-2xl font-bold text-foreground`}
            style={headingFontStyle()}
          >
            {title}
            {count !== undefined && (
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-content-muted">
                {count}
              </span>
            )}
          </h3>
          {description && (
            <div className="mt-1 text-sm text-muted-foreground">
              {description}
            </div>
          )}
        </div>
        {leftActions && (
          <div className="flex items-center gap-2">{leftActions}</div>
        )}
      </div>
      {/* Stats are their own row item, never inside the controls cluster: there
          their width is capped by whatever the title leaves over, and a
          right-aligned box narrower than its contents overflows to the LEFT —
          across the title. As a row item they shrink only once the title has
          grown down to its own min-content, and PanelHeaderStats_ is a
          flex-wrap row, so shrinking means wrapping onto another line. Floored
          at the widest single stat. */}
      {headerStats && headerStats.length > 0 && (
        <PanelHeaderStats_ items={headerStats} />
      )}
      {/* The cluster grows into whatever the title leaves over, so the
          ControlBar's overflow ActionBar has a real width to measure and extra
          actions fold into the kebab. The basis and the kebab-wide floor are
          what keep that width real: on a basis of 0 a title wide enough to fill
          the row is served first and the cluster lands at zero, which doesn't
          clip the buttons — it spills them left across the title. Reserving the
          room makes the title wrap instead. With stats the title block has a
          zero basis of its own, so the two share the slack rather than the
          title taking it first — that share is what ActionBar measures, so it
          still grows with the panel instead of sitting at the basis. */}
      {hasControls && (
        <div className="flex min-w-11 shrink grow basis-32 items-center justify-end gap-5">
          <ControlBar
            filters={headerFilters}
            actions={headerActions}
            menu={menu}
            actionCollapse="responsive"
          />
        </div>
      )}
    </div>
  );
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function Panel({
  title,
  description,
  count,
  headerStats,
  headerFilters,
  headerActions,
  menu,
  leftActions,
  filterBar,
  statsBar,
  children,
  collapsible,
  accent = "blue",
  defaultOpen = true,
  fullBleed = false,
  edgeToEdge = false,
  accentColor,
  stickyHeader = false,
  collapseOnNarrow = false,
  cornerNumber,
}: PanelProps) {
  // Comfortable density (Cargo card): 1px border, tighter radius, a
  // subtle shadow, and the surface.
  // A panel that shows a stat band above its body isn't a pure edge-to-edge
  // table panel — , inset the body so a table's header rule aligns under
  // the (inset) stats instead of running to the panel border, and the last row
  // gets bottom padding.
  const hasStatsBar = !!(statsBar && statsBar.length > 0);
  const bodyBleeds = fullBleed && !hasStatsBar;
  // Edge-to-edge: body drops its horizontal gutter (px-0) and the Table (told
  // via PageEdgeContext) moves the gutter into its cells, so rows span the full
  // card. Body vertical padding is kept.
  const bodyEdgeToEdge = bodyBleeds && edgeToEdge;
  const bleedBodyX = bodyEdgeToEdge ? "px-0" : "px-6";
  if (collapsible) {
    const showAccent = accent !== "none";
    const hasControls = !!headerFilters || !!headerActions || !!menu;
    const hasStats = (headerStats?.length ?? 0) > 0;
    // Same last resort as the non-collapsible header, for the same reason: the
    // stats shrink to their widest single stat and no further, so without a
    // line to drop onto they and the kebab get pushed outside the card. This
    // title is shrink-0 and whitespace-nowrap, so unlike the other header it is
    // its FULL width, not just its longest word, that sets where the break
    // falls. Written out in full because Tailwind's JIT can't see a spliced
    // class name.
    const headerRowClass = hasStats
      ? "flex flex-wrap items-center justify-between gap-3 px-6 py-3"
      : "flex items-center justify-between gap-3 px-6 py-3";
    return (
      <CollapsiblePrimitive.Root defaultOpen={defaultOpen}>
        <div
          className={`rounded-none border shadow-[6px_6px_0px_0px_var(--muted)] ${showAccent ? "border-l-4" : ""} border-border-strong bg-card ${accentColor || !showAccent ? "" : accentBorder[accent]}`}
          {...(accentColor ? { style: { borderLeftColor: accentColor } } : {})}
        >
          <div
            className={`${headerRowClass} ${stickyHeader ? `sticky top-0 ${layerClass.raised} rounded-t-none bg-card` : ""}`}
          >
            <div className="flex shrink-0 flex-col gap-0.5">
              <div className="flex items-center gap-5">
                <CollapsiblePrimitive.Trigger className="group flex items-center gap-3 text-left transition-colors hover:opacity-70">
                  <span className="shrink-0 text-content-faint group-data-[state=open]:hidden">
                    <Icon icon={CaretRightIcon} color="text-content-faint" />
                  </span>
                  <span className="shrink-0 text-content-faint group-data-[state=closed]:hidden">
                    <Icon icon={CaretDownIcon} color="text-content-faint" />
                  </span>
                  <span className="text-sm font-bold whitespace-nowrap text-foreground">
                    {title}
                  </span>
                  {count !== undefined && (
                    <span
                      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                        accentColor
                          ? ""
                          : showAccent
                            ? accentBadge[accent]
                            : "bg-muted text-content-muted"
                      }`}
                      {...(accentColor
                        ? {
                            style: {
                              backgroundColor: accentColor + "20",
                              color: accentColor,
                            },
                          }
                        : {})}
                    >
                      {count}
                    </span>
                  )}
                </CollapsiblePrimitive.Trigger>
                {leftActions && (
                  <div className="flex items-center gap-2">{leftActions}</div>
                )}
              </div>
              {description && (
                <span className="ml-7 text-xs text-content-secondary">
                  {description}
                </span>
              )}
            </div>
            {/* Stats are their own row item here too, but shrinkable: the
                collapsible title is nowrap and can't yield, so the stats give
                up the room instead and wrap onto a second line. Their floor is
                the widest single stat, which keeps them from ever being
                narrower than their contents. */}
            {headerStats && headerStats.length > 0 && (
              <PanelHeaderStats_ items={headerStats} />
            )}
            {/* flex-1 so the overflow ActionBar has a width to measure and
                extra actions fold into the kebab instead of spilling out, with
                a kebab-wide floor so the cluster can never collapse to zero and
                spill them left across the stats. */}
            {hasControls && (
              <div className="flex min-w-11 flex-1 items-center justify-end gap-5">
                <ControlBar
                  filters={headerFilters}
                  actions={headerActions}
                  menu={menu}
                  actionCollapse="responsive"
                />
              </div>
            )}
          </div>
          <CollapsiblePrimitive.Content className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
            {filterBar && (
              <div className={`border-t border-border px-6 py-2`}>
                <ControlBar filters={filterBar} />
              </div>
            )}
            {statsBar && statsBar.length > 0 && (
              <PanelStats_ stats={statsBar} />
            )}
            <PanelContext.Provider value={bodyBleeds}>
              <PageEdgeContext.Provider value={bodyEdgeToEdge}>
                <div
                  className={`border-t border-border ${bodyBleeds ? `${bleedBodyX} pb-3` : "px-6 py-4"}`}
                >
                  {children}
                </div>
              </PageEdgeContext.Provider>
            </PanelContext.Provider>
          </CollapsiblePrimitive.Content>
        </div>
      </CollapsiblePrimitive.Root>
    );
  }

  // A panel with no title (and no other header content) is a plain bordered
  // card — the header band is dropped entirely and the body carries its own top
  // padding so its content isn't flush to the top border.
  const hasHeader =
    title !== undefined ||
    description !== undefined ||
    count !== undefined ||
    (headerStats?.length ?? 0) > 0 ||
    !!headerFilters ||
    !!headerActions ||
    !!menu ||
    !!leftActions;
  const shownCornerNumber = visibleCornerNumber({
    cornerNumber,
    hasHeader,
    hasFilterBar: !!filterBar,
    hasStatsBar,
  });

  return (
    <PanelCornerNumber_ cornerNumber={shownCornerNumber}>
      <div
        className={`rounded-none border border-border-strong bg-card shadow-[6px_6px_0px_0px_var(--muted)]${
          collapseOnNarrow ? collapseFrameClasses : ""
        }`}
      >
        {hasHeader && (
          <PanelHeader_
            title={title}
            description={description}
            count={count}
            headerStats={headerStats}
            headerFilters={headerFilters}
            headerActions={headerActions}
            menu={menu}
            leftActions={leftActions}
          />
        )}
        {filterBar && (
          <div className="px-6 py-2">
            <ControlBar filters={filterBar} />
          </div>
        )}
        {statsBar && statsBar.length > 0 && <PanelStats_ stats={statsBar} />}
        <PanelContext.Provider value={bodyBleeds}>
          <PageEdgeContext.Provider value={bodyEdgeToEdge}>
            <div
              className={`${
                bodyBleeds
                  ? // Full-bleed body: a horizontal gutter so the table's rules sit
                    // inset from the panel border, plus a little bottom breathing
                    // room so the last row isn't flush to the edge. Headerless
                    // panels also need top room so the first row isn't flush to
                    // the top border. (edge-to-edge drops the gutter to px-0.)
                    hasHeader
                    ? `${bleedBodyX} pb-3`
                    : `${bleedBodyX} py-3`
                  : // collapseOnNarrow panels are the customer flow's sections:
                    // when there IS room they breathe with Cargo's original 32px
                    // gutter, and shed to zero inside .narrow-cards. Admin panels
                    // keep the denser default.
                    collapseOnNarrow
                    ? hasHeader
                      ? "px-8 pb-8"
                      : "px-8 py-8"
                    : hasHeader
                      ? "px-6 pb-5"
                      : "px-6 py-5"
              }${collapseOnNarrow ? collapseBodyClasses : ""}`}
            >
              <PanelCornerNumberSpace_
                cornerNumber={shownCornerNumber}
                collapseOnNarrow={collapseOnNarrow}
              >
                {children}
              </PanelCornerNumberSpace_>
            </div>
          </PageEdgeContext.Provider>
        </PanelContext.Provider>
      </div>
    </PanelCornerNumber_>
  );
}
